import { mediacoreApi } from '@apis/mediacoreApi'
import {
  createOneMutation,
  deleteByIdMutation,
  getAllByClientIdQuery,
  getByIdAndClientIdQuery,
  pkSubEndpointMutation,
  updateByIdMutation,
} from '@apis/mediacoreQueries'
import { SiteV2 } from '@types'

const sitesV2url = 'site/sitios-v2'

const sitesV2Api = mediacoreApi.injectEndpoints({
  endpoints: (builder) => ({
    getSiteV2ById: builder.query<SiteV2, { id: number; clientId: number }>({
      query: getByIdAndClientIdQuery(sitesV2url),
      providesTags: (_result, _error, { id }) => [{ type: 'SiteV2', id }],
    }),
    getSitesV2: builder.query<SiteV2[], { clientId: number; search?: string }>({
      query: getAllByClientIdQuery(sitesV2url),
      providesTags: ['SiteV2'],
    }),
    createSiteV2: builder.mutation<SiteV2, { data: Partial<SiteV2> }>({
      query: createOneMutation(sitesV2url),
      async onQueryStarted(_data, { dispatch, queryFulfilled }) {
        try {
          const { data: createdSite } = await queryFulfilled
          dispatch(
            sitesV2Api.util.updateQueryData('getSitesV2', { clientId: createdSite.client! }, (draftSites) => {
              draftSites.unshift(createdSite)
              return draftSites
            }),
          )
        } catch {}
      },
      invalidatesTags: ['SiteV2GetData'],
    }),
    deleteSite: builder.mutation<void, { id: number; clientId: number }>({
      query: deleteByIdMutation(sitesV2url),
      async onQueryStarted({ id, clientId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            sitesV2Api.util.updateQueryData('getSitesV2', { clientId }, (draftSites) => {
              return draftSites.filter((draftSite) => draftSite.id !== id)
            }),
          )
        } catch {}
      },
    }),
    updateSiteV2: builder.mutation<SiteV2, { id: number; data: Partial<SiteV2> }>({
      query: updateByIdMutation(sitesV2url),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedSite } = await queryFulfilled
          dispatch(
            sitesV2Api.util.updateQueryData('getSiteV2ById', { id, clientId: updatedSite.client! }, (draft) => {
              Object.assign(draft, updatedSite)
            }),
          )
          dispatch(
            sitesV2Api.util.updateQueryData('getSitesV2', { clientId: updatedSite.client! }, (draftSites) => {
              return draftSites.map((draftSite) => (draftSite.id !== id ? draftSite : updatedSite))
            }),
          )
        } catch {}
      },
      invalidatesTags: ['SiteV2GetData'],
    }),
    uploadSiteV2Images: builder.mutation<
      SiteV2,
      {
        id: number
        data: {
          files: File[]
          file_name: string //
          field: string
        }
      }
    >({
      query: pkSubEndpointMutation('site/sitios-v2', 'galeria'),
      invalidatesTags: ['SiteV2', 'SiteV2GetData'],
    }),
    linkSiteV2File: builder.mutation<
      { status: string },
      {
        id: number
        data: {
          file: string
          field: string
        }
      }
    >({
      query: pkSubEndpointMutation('site/sitios-v2', 'galeria'),
      invalidatesTags: ['SiteV2', 'SiteV2GetData'],
    }),
    signSiteV2Upload: builder.mutation<
      { url: string; object_name: string; public_url: string },
      { id: number; data: { file_name: string; content_type: string; client: number } }
    >({
      query: pkSubEndpointMutation('site/sitios-v2', 'sign_upload'),
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetSiteV2ByIdQuery,
  useGetSitesV2Query,
  useCreateSiteV2Mutation,
  useUpdateSiteV2Mutation,
  usePrefetch: usePrefetchSitesV2,
  useUploadSiteV2ImagesMutation,
  useLinkSiteV2FileMutation,
  useSignSiteV2UploadMutation,
  useDeleteSiteMutation,
} = sitesV2Api
